import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import phoneNumberValidator from "@onsip/common/libraries/validators/phone-number-validators";
import { callerIdNamePhoneValidator } from "../validators/caller-id-name-phone.validatos";
import { views } from "@onsip/web/app/phone/views";
import { Organization } from "@onsip/common/services/api/resources/organization/organization";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { AbstractControl, NonNullableFormBuilder, ValidatorFn } from "@angular/forms";
import { OrganizationService } from "@onsip/common/services/api/resources/organization/organization.service";
import { GatewayUserService } from "@onsip/common/services/api/resources/gatewayUser/gateway-user.service";
import { BehaviorSubject, Subscription, take } from "rxjs";
import { MatDialog } from "@angular/material/dialog";
import { ModalMaterialComponent } from "@onsip/web/features/shared/components/modal/modal-material.component";
import { TranslateService } from "@ngx-translate/core";

@Component({
  selector: "onsip-default-caller-id-edit",
  templateUrl: "./default-caller-id-edit.component.html",
  styleUrls: ["./default-caller-id-edit.component.scss"],
  standalone: false
})
export class DefaultCallerIdEditComponent implements OnInit, OnDestroy {
  @Input() organization!: Organization;
  @Output() editPanel = new EventEmitter<void>();
  callerIdForm = this.fb.group(
    {
      name: ["", [this.specialCharValidator()]],
      phone: ["", phoneNumberValidator()],
      cascade: [false]
    },
    { validators: [callerIdNamePhoneValidator()] }
  );
  views = views;

  private unsubscriber = new Subscription();
  loadingStateComplete$ = new BehaviorSubject(true);

  constructor(
    private organizationService: OrganizationService,
    private gatewayUserService: GatewayUserService,
    private snackbar: SnackbarService,
    private fb: NonNullableFormBuilder,
    private dialog: MatDialog,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    this.initFormData();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  onSubmit(): void {
    if (this.callerIdForm.invalid) {
      this.callerIdForm.markAllAsTouched();
      return;
    }

    const formValue = this.callerIdForm.getRawValue();
    this.loadingStateComplete$.next(false);

    this.organizationService
      .organizationEditCallerId({
        CallerIdName: formValue.name,
        CallerIdNumber: formValue.phone,
        Cascade: formValue.cascade
      })
      .then(response => {
        if (response.status === "error") {
          this.snackbar.openSnackBar(response.data.message, "error");
          this.organizationService.clearErrors();
        } else {
          this.snackbar.openSnackBar("Default caller ID has been updated", "success");
          this.callerIdForm.patchValue({ cascade: false });
          this.gatewayUserService.dispose();
          this.editPanel.next();
        }
        this.loadingStateComplete$.next(true);
      });
  }

  cancel(): void {
    this.unsubscriber.add(
      this.dialog
        .open(ModalMaterialComponent, {
          panelClass: ["mat-typography", "onsip-dialog-universal-style"],
          data: {
            title: this.translate.instant("ONSIP_I18N.CANCEL_EDITS"),
            message: this.translate.instant(
              "ONSIP_I18N.ANY_CHANGES_YOU_HAVE_MADE_WILL_NOT_BE_SAVED"
            ),
            primaryBtnText: this.translate.instant("ONSIP_I18N.OK"),
            secondaryBtnText: this.translate.instant("ONSIP_I18N.KEEP_EDITING"),
            primaryBtnFlat: true
          }
        })
        .afterClosed()
        .pipe(take(1))
        .subscribe(retObj => {
          if (retObj?.doPrimaryAction) {
            this.editPanel.emit();
          }
        })
    );
  }

  private initFormData(): void {
    this.organizationService.organizationRead();

    this.callerIdForm.patchValue({
      name: this.organization.callerIdName,
      phone: this.organization.callerIdNumber
    });
  }

  private specialCharValidator(): ValidatorFn {
    // can't contain special characters
    return (control: AbstractControl): { [key: string]: any } | null => {
      const regex = new RegExp("[~!@#$%^&*()=+,:`]", "gi");
      const containsRegex = regex.test(control.value);
      // eslint-disable-next-line no-null/no-null
      return containsRegex ? { specialChar: { value: control.value } } : null;
    };
  }
}
